<template>
  <div v-if="summary" class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,2.4fr)] gap-4">
    <div
      class="bg-primary-light/30 rounded-xl border border-primary-light p-5 flex flex-col gap-3"
      data-test="pending-statements"
    >
      <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">{{ t("pending_statements") }}</span>
      <div>
        <p class="text-3xl font-bold tabular-nums" data-test="pending-statements-amount">
          {{ formatPrice(summary.pendingStatements.amount) }}
        </p>
        <p class="text-sm text-gray-500 mt-1" data-test="pending-statements-count">
          {{ t("statements_count", summary.pendingStatements.count) }}
        </p>
      </div>
    </div>

    <div
      class="bg-primary-light/30 rounded-xl border border-primary-light p-5 flex flex-col gap-3"
      data-test="unpaid-bills"
    >
      <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">{{ t("unpaid_bills") }}</span>
      <div class="flex items-baseline gap-3">
        <p class="text-3xl font-bold tabular-nums" data-test="unpaid-bills-amount">
          {{ formatPrice(summary.unpaidBills.amount) }}
        </p>
        <p class="text-sm text-gray-500" data-test="unpaid-bills-count">
          {{ t("bills_count", summary.unpaidBills.count) }}
        </p>
      </div>
      <div class="flex h-2 rounded-full overflow-hidden bg-white">
        <div
          v-for="mandate in mandates"
          :key="mandate.status"
          :class="mandate.style.segment"
          :style="{ width: `${mandate.share}%` }"
          :data-test="`unpaid-bills-segment-${mandate.status}`"
        ></div>
      </div>
      <div class="grid grid-cols-3 divide-x divide-primary-light">
        <div
          v-for="mandate in mandates"
          :key="mandate.status"
          class="px-3 py-2"
          :class="mandate.style.card"
          :data-test="`unpaid-bills-${mandate.status}`"
        >
          <div class="flex items-center gap-2 text-sm text-gray-600">
            <span class="w-2 h-2 rounded-full" :class="mandate.style.segment"></span>
            <span :class="mandate.style.label">{{ t(`mandate_status.${mandate.status}`) }}</span>
            <button
              v-if="mandate.status === 'notSigned'"
              type="button"
              class="ml-auto text-xs font-medium text-orange-700 hover:underline"
              data-test="show-to-remind"
              @click="$emit('show-to-remind')"
            >
              {{ t("to_remind") }}
            </button>
          </div>
          <p class="text-lg font-semibold tabular-nums mt-1" :class="mandate.style.amount" data-test="amount">
            {{ formatPrice(mandate.amount) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { formatPrice } from "@estaly/ui";

// Full class names (not built by concatenation) so Tailwind keeps them.
const MANDATE_STYLES = {
  signed: { segment: "bg-green-500", card: "", label: "", amount: "" },
  sent: { segment: "bg-indigo-400", card: "", label: "", amount: "" },
  notSigned: {
    segment: "bg-orange-500",
    card: "bg-orange-50 ring-1 ring-orange-200 rounded-lg",
    label: "font-semibold text-orange-900",
    amount: "font-bold text-orange-900",
  },
};

export default {
  name: "InvoicesSummary",

  props: {
    summary: {
      type: Object,
      default: null,
    },
  },

  emits: ["show-to-remind"],

  setup() {
    const { t } = useI18n();
    return { t };
  },

  computed: {
    mandates() {
      const total = parseFloat(this.summary.unpaidBills.amount);

      return Object.entries(this.summary.unpaidBills.amountByMandateStatus).map(([status, amount]) => ({
        status,
        amount,
        share: total > 0 ? (parseFloat(amount) / total) * 100 : 0,
        style: MANDATE_STYLES[status],
      }));
    },
  },

  methods: {
    formatPrice,
  },
};
</script>

<i18n src="./InvoicesSummary.i18n.json"></i18n>
